<template>
  <v-row>
    <v-col md="8" sm="12">
      <v-card>
        <v-card-title>{{ getPageTitle() }}</v-card-title>
        <v-container v-if="!wallpaperLoaded" class="text-center" style="height: 200px;">
          <VProgressCircular indeterminate size="48">
          </VProgressCircular>
        </v-container>
        <v-img v-else-if="!wallpapers.length" contain :aspect-ratio="16 / 10" :src="tenant.logo.full_hd"
          style="max-height: 200px"></v-img>
        <v-img v-else-if="wallpapers.length === 1" :src="wallpapers[0].image.full_hd" style="max-width: 100%"
          class="mb-10"></v-img>
        <v-carousel v-else class="mb-10">
          <v-carousel-item v-for="(item, i) in wallpapers" :key="i" :src="item.image.full_hd"
            reverse-transition="fade-transition" transition="fade-transition"></v-carousel-item>
        </v-carousel>

        <v-card-text>
          <v-treeview open-all :items="pages" item-key="id" item-text="title" item-children="children" ref="treeview">
            <template v-slot:label="{ item }">
              <div class="d-flex justify-space-between companypage-list-item">
                <router-link
                  :to="{ name: 'company-page', params: { id: item.id }, query: { category: $route.query.category } }">{{
                    item.title }}</router-link>

                <v-icon v-if="item.require_acknowledgment && tenant.settings.acknowledgments_enabled" class="ml-3"
                  :title="$t('Lukukuittaus vaaditaan')" :color="item.read ? 'success' : 'error'">
                  {{ icons.mdiDraw }}
                </v-icon>
              </div>
            </template>
          </v-treeview>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, ref, nextTick, watch } from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import api from "@/api";
import useAuth from "@/useAuth";
import useMenus from "@/@core/utils/useMenus";
import { i18n } from "@/plugins/i18n";
import { mdiDraw } from "@mdi/js";
import { useRouter } from "@/@core/utils";

export default {
  setup() {

    const pages = ref([]);
    const { t } = useTranslations();
    const { tenant } = useAuth();
    const { getTenantTerm } = useMenus();
    const { route } = useRouter();
    const treeview = ref(null);
    const open = ref([]);
    const wallpapers = ref([]);
    const wallpaperLoaded = ref(false);

    const fetchPages = async () => {
      const categoryId = route.value.query.category || null;
      const response = await api.getPages(categoryId);

      if (response.ok) {
        pages.value = response.data;

        nextTick(() => {
          treeview.value.updateAll(true);
        });
      } else {
        alert(response.error);
      }
    };

    const getPageTitle = () => {
      // If there's a category query param, find the category and use its title
      const categoryId = route.value.query.category;
      if (categoryId && tenant.value.page_categories) {
        const category = tenant.value.page_categories.find(cat => cat.id === parseInt(categoryId));
        if (category) {
          return category.title;
        }
      }

      // Fallback to default "Our Company" title
      const tenantTerm = getTenantTerm(
        "our_company",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? t("Meidän yritys");
    };

    const fetchWallpapers = async () => {
      const categoryId = route.value.query.category || null;
      const response = await api.getWallpapers(categoryId);
      if (response.ok) {
        wallpapers.value = response.data;
      }
      wallpaperLoaded.value = true;
    }

    // Watch for category changes in the route query
    watch(
      () => route.value.query.category,
      () => {
        fetchPages();
        fetchWallpapers();
      }
    );

    onMounted(() => {
      fetchPages();
      fetchWallpapers();
    });

    return {
      treeview,
      open,
      getPageTitle,
      pages,
      tenant,
      t,
      wallpaperLoaded,
      wallpapers,
      icons: {
        mdiDraw
      }
    };
  },
};
</script>

<style scoped>
.companypage-list-item {
  padding: 4px 10px;
  border-bottom: 1px solid transparent;
}

.companypage-list-item:hover {
  border-bottom: 1px dashed rgb(146, 146, 146);
}

.v-treeview-node__label a,
.v-treeview-node__children a {
  text-decoration: none;
}

.v-treeview-node__label a {
  font-size: 1.3em;
}

.v-treeview-node__children a {
  font-size: 1.2em;
}
</style>
